import type { FormHTMLAttributes, ReactNode } from 'react';

import type { FieldValues, SubmitErrorHandler, SubmitHandler, UseFormReturn } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';

import type { BackendErrorResponse } from 'api/types';

import type { BackendFieldAliases, BackendFormFieldErrorEvent } from './useBackendFormErrors';
import { useBackendFormErrors } from './useBackendFormErrors';

type FormProps<TFieldValues extends FieldValues> = Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  formMethods: UseFormReturn<TFieldValues>;
  // Required so a form can't be wired up without backend-error handling. Pass `null` or
  // `undefined` explicitly when the mutation hasn't run yet.
  error: BackendErrorResponse | null | undefined;
  onSubmit: SubmitHandler<TFieldValues>;
  onInvalid?: SubmitErrorHandler<TFieldValues>;
  onFieldError?: (event: BackendFormFieldErrorEvent<TFieldValues>) => void;
  parseBackendErrorMessage?: (error: BackendErrorResponse) => string | undefined;
  /**
   * Maps a backend field name to the registered RHF field that should receive its error.
   *
   * Use only when the FE field holds an object value (e.g. an autocomplete result) and a
   * rename would lose information. For scalar 1:1 matches, rename the FE field to match
   * the BE name instead — no alias needed.
   *
   * @example
   * ```tsx
   * fieldAliases={{ company_name: 'company', provider_name: 'payrollProvider' }}
   * ```
   */
  fieldAliases?: BackendFieldAliases<TFieldValues>;
  children: ReactNode;
};

export const Form = <TFieldValues extends FieldValues>({
  formMethods,
  error,
  onSubmit,
  onInvalid,
  onFieldError,
  parseBackendErrorMessage,
  fieldAliases,
  children,
  ...formProps
}: FormProps<TFieldValues>) => {
  useBackendFormErrors<TFieldValues>({
    formMethods,
    error,
    onFieldError,
    parseBackendErrorMessage,
    fieldAliases,
  });

  return (
    <FormProvider {...formMethods}>
      {/* The browser's own bubbles fight the schema: they preempt submit with their own copy,
          point at whichever field they reach first, and say nothing a rule here has not already
          said better. Validation is react-hook-form's job; `required` still marks the label. */}
      <form noValidate {...formProps} onSubmit={formMethods.handleSubmit(onSubmit, onInvalid)}>
        {children}
      </form>
    </FormProvider>
  );
};
